import { pluralize } from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

import {
  OrderStatus,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { getUrlObject } from '../../utils/url';
import { SessionOption, useScheduleModal } from '../coach/modals/ScheduleModal';
import { usePhoneNumberModal } from '../sms/PhoneNumberModal';

import CoachingSession from './CoachingSession';
import { CoachMessageCtaGetCoachByIdDocument } from './__generated-gql-types__/CoachMessageCTA.generated';
import {
  type UpcomingSessionsCtaCoachingSessionFragment,
  useUpcomingSessionsCtaGetCoachByIdQuery,
} from './__generated-gql-types__/UpcomingSessionsCTA.generated';

interface UpcomingSessionsCTAProps {
  className?: string;
  coachId: Scalars['CoachUrn']['output'];
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  blocked?: boolean;
}

const UpcomingSessionsCTA: React.FC<UpcomingSessionsCTAProps> = ({
  className,
  coachId,
  categoryUrn,
  blocked,
}) => {
  const router = useRouter();

  const { data: coachData } = useUpcomingSessionsCtaGetCoachByIdQuery({
    variables: {
      id: coachId,
    },
  });

  const { setOpen: setIsSchedulingOpen, Modal: ScheduleModal } =
    useScheduleModal();
  const {
    openModalIfNecessary: openPhoneNumberModalIfNecessary,
    Modal: PhoneNumberModal,
  } = usePhoneNumberModal();

  const relationship = coachData?.coach?.coachingRelationship;
  const upcomingEnrolledSessions = relationship?.upcomingEnrolledSessions ?? [];

  const [coachingSessionForReschedule, setCoachingSessionForReschedule] =
    useState<Possible<UpcomingSessionsCtaCoachingSessionFragment>>(null);

  const onReschedule = (
    session: UpcomingSessionsCtaCoachingSessionFragment,
  ) => {
    setCoachingSessionForReschedule(session);
    setIsSchedulingOpen(true);
  };

  return upcomingEnrolledSessions.length > 0 && coachData?.coach ? (
    <>
      <div
        className={`flex flex-col justify-start space-y-3 rounded-lg border p-4 ${className}`}
      >
        <h1 className="text-lg font-medium">Your upcoming sessions</h1>
        <>
          {upcomingEnrolledSessions
            .slice(
              0,
              upcomingEnrolledSessions.length > 2
                ? 2
                : upcomingEnrolledSessions.length,
            )
            .map((session) => (
              <CoachingSession
                key={session.id}
                session={session}
                onReschedule={() => onReschedule(session)}
                blocked={blocked}
              />
            ))}
          {upcomingEnrolledSessions.length > 2 ? (
            <button
              className="w-32 font-medium text-leland-primary"
              onClick={() =>
                router.push(
                  getUrlObject('/inbox', {
                    coach: coachId,
                  }),
                )
              }
              id="upcoming_sessions_cta:view_all"
            >
              View all {pluralize(upcomingEnrolledSessions.length, 'session')}
            </button>
          ) : null}
        </>
      </div>

      <ScheduleModal
        coach={coachData.coach}
        categoryUrn={categoryUrn}
        defaultStartTime={coachingSessionForReschedule?.startAt}
        coachingSession={coachingSessionForReschedule}
        refetchQueries={[CoachMessageCtaGetCoachByIdDocument]}
        {...(coachingSessionForReschedule?.content.__typename ===
          'OneOnOneSession' &&
        coachingSessionForReschedule.content.order &&
        [
          OrderStatus.INTRO_CALL_ACCEPTED,
          OrderStatus.INTRO_CALL_REQUESTED,
        ].includes(coachingSessionForReschedule.content.order.status)
          ? {
              sidebarLocked: true,
              defaultSessionType: SessionOption.INTRO_CALL,
            }
          : {})}
        onSuccessModalDismiss={openPhoneNumberModalIfNecessary}
      />
      <PhoneNumberModal />
    </>
  ) : null;
};

export default UpcomingSessionsCTA;
